// API схем шаблонов и мастера подключения (010: contracts/api.md, «Маршруты», «Схемы»; data-model.md,
// «Матрица операций»). Схема — запись модуля documents с номером (uuid) для журнала и If-Match,
// ключом схемы (`schemaId`, как в версиях документов) и неизменяемыми версиями тела.
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema, VersionSchema } from '../kernel/common.ts';
import { RowKindCorrectionsSchema, TemplateSchemaSchema } from '../protocol/template.ts';
import { CreateTemplateBodySchema } from '../protocol/onboarding.ts';

/** Действует или в архиве: архивная схема шапку не узнаёт (FR-036). */
export const templateStates = ['active', 'archived'] as const;
export type TemplateState = (typeof templateStates)[number];
export const TemplateStateSchema = z.enum(templateStates);

/** file — затравка из файла установки, wizard — подключена мастером (research, Р2). */
export const templateSources = ['file', 'wizard'] as const;
export type TemplateSource = (typeof templateSources)[number];
export const TemplateSourceSchema = z.enum(templateSources);

export const templateSourceLabels = {
  file: 'из файла установки',
  wizard: 'мастер',
} as const satisfies Record<TemplateSource, string>;

/** Строка списка схем (FR-001, сценарий 8). */
export const TemplateRowSchema = z.object({
  id: IdSchema,
  /** Ключ схемы: у схем файла установки — их id, у схем мастера — «w-» и 8 знаков. */
  schemaId: z.string().regex(/^[a-z0-9-]+$/),
  title: z.string(),
  state: TemplateStateSchema,
  source: TemplateSourceSchema,
  /** Подписи шапки через « · ». */
  header: z.string(),
  /** Действующая версия схемы. */
  version: z.number().int().min(1),
  confirmedBy: PersonRefSchema.nullable(),
  confirmedAt: TimestampSchema,
  /** Сколько версий документов разобрано по схеме. */
  parsedVersions: z.number().int().min(0),
  /** Версия записи для If-Match (FR-038). */
  recordVersion: VersionSchema,
});
export type TemplateRow = z.infer<typeof TemplateRowSchema>;

export const TemplateListSchema = z.object({ items: z.array(TemplateRowSchema) });

export const templateSorts = ['title', '-updatedAt'] as const;
export const TEMPLATE_SEARCH_MAX = 200;

export const TemplateListQuerySchema = z.object({
  /** Поиск по названию и подписям шапки без учёта регистра. */
  q: z.string().trim().min(1).max(TEMPLATE_SEARCH_MAX).optional(),
  state: TemplateStateSchema.default('active'),
  sort: z.enum(templateSorts).default('title'),
});

/** Версия схемы: номер, что изменилось, сколько решил ИИ и администратор, кто и когда. */
export const TemplateVersionBriefSchema = z.object({
  number: z.number().int().min(1),
  change: z.string(),
  answers: z.object({ ai: z.number().int().min(0), admin: z.number().int().min(0) }),
  by: PersonRefSchema.nullable(),
  at: TimestampSchema,
});

/** Карточка схемы (макет 3б): тело действующей версии, лист-образец, история версий. */
export const TemplateCardSchema = TemplateRowSchema.extend({
  body: TemplateSchemaSchema,
  sample: z
    .object({ fileName: z.string(), sheet: z.string(), headerRow: z.number().int().min(1) })
    .nullable(),
  versions: z.array(TemplateVersionBriefSchema),
});
export type TemplateCard = z.infer<typeof TemplateCardSchema>;

/** «Изменить карту» открывает мастер на листе-образце с картой схемы (FR-034). */
export const OnboardingAnalyzeQuerySchema = z.object({ templateId: IdSchema.optional() });

/**
 * Новая версия: новая карта из мастера (без названия) или только поправки — «Убрать» поправку
 * в карточке (FR-034, FR-031).
 */
export const NewTemplateVersionBodySchema = z.union([
  CreateTemplateBodySchema.omit({ title: true }),
  z.object({ rowKinds: RowKindCorrectionsSchema }),
]);
export type NewTemplateVersionBody = z.infer<typeof NewTemplateVersionBodySchema>;

/** Правка в карточке: название — без новой версии, слова — новая версия (FR-035). */
export const UpdateTemplateBodySchema = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
    pauseWords: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    doneWords: z.array(z.string().trim().min(1).max(40)).min(1).max(20).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Нечего менять' });
export type UpdateTemplateBody = z.infer<typeof UpdateTemplateBodySchema>;
